import UserFormDetail from 'forms/User/UserFormDetail'
import useFetch from 'hooks/useFetch'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { ImSpinner9 } from 'react-icons/im'
import { LuMoveLeft } from 'react-icons/lu'
import { toast } from 'react-toastify'
import { useLocation } from 'wouter'

export default function UserViewDetail({ params }) {
  // Wouter - useLocation() permite cambiar de pantalla
  const [, setLocation] = useLocation()

  // useFetch(): hooks personalizados para realizar solicitudes GET, POST, PUT, DELETE.
  const [fetchedUser, fetchUser] = useFetch()
  const [updatedUser, updateUser] = useFetch()
  const [deletedUser, deleteUser] = useFetch()

  // Variables:
  const isNew = params?.id === 'new'
  const [fields, setFields] = useState({
    id: 0,
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    password_confirmation: ''
  })

  // CRUD: GET (Recoger datos del usuario con la API)
  useEffect(() => {
    // Solo si no es un nuevo usuario, hacer la solicitud GET
    if (!isNew) {
      const apiCallGet = {
        url: `users/${params.id}`,
        method: 'GET',
        messageKo: '¡Error al recoger Usuario!'
      }
      fetchUser(apiCallGet) // Llama a la función del hook personalizado para realizar la solicitud GET
    }
  }, []) // Este useEffect se ejecutará una vez al principio y en cada renderización del componente

  // Recoger datos API: GET (Cuando se obtiene la respuesta, se actualizan los campos)
  useEffect(() => {
    // Si los datos son recibidos correctamente
    if (fetchedUser.data) {
      setFields({
        id: fetchedUser.data.id,
        username: fetchedUser.data.username,
        email: fetchedUser.data.email,
        first_name: fetchedUser.data.first_name,
        last_name: fetchedUser.data.last_name,
        password: '',
        password_confirmation: ''
      })
    }
  }, [fetchedUser.data]) // Este useEffect se ejecutará cada vez que 'fetchedUser.data' cambie

  // CRUD: POST / PUT (Enviar datos del formulario)
  const handleSubmit = async (values) => {
    let url = `users`
    let method = 'POST'

    // Si no es un nuevo usuario, actualizar datos con PUT
    if (!isNew) {
      method = 'PUT'
      url += `/${params.id}`

      // Si la contraseña está vacía, no se envía
      if (values.password === '') {
        values.password = null
      }
    }

    const apiCallPost = {
      url,
      method,
      body: values // El cuerpo de la solicitud contiene los valores del formulario
    }
    await updateUser(apiCallPost) // Realiza la solicitud POST o PUT
  }

  // Mostrar mensaje API: POST / PUT (Muestra notificación de éxito o error)
  useEffect(() => {
    // Si la solicitud ha sido realizada
    if (updatedUser.isCalled) {
      // Función para mostrar un mensaje de éxito
      const successMessage = (message) =>
        toast.success(message, {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light'
        })

      // Función para mostrar un mensaje de error
      const errorMessage = (error) =>
        toast.error(error, {
          position: 'top-center',
          autoClose: 5000,
          closeOnClick: false,
          hideProgressBar: false,
          pauseOnHover: true,
          progress: undefined,
          theme: 'light'
        })

      // Si hay un mensaje de éxito
      if (updatedUser.message) {
        successMessage(updatedUser.message)
        setLocation('/users/grid')
      }

      // Si hay un mensaje de error
      if (updatedUser.error) {
        errorMessage(updatedUser.error)
      }
    }
  }, [updatedUser.loading]) // Este useEffect se ejecutará cada vez que 'updatedUser.loading' cambie

  // CRUD: DELETE (Eliminar un usuario)
  const handleDelete = async (id) => {
    const apiCallDelete = {
      url: `users/${id}`,
      method: 'DELETE'
    }
    await deleteUser(apiCallDelete) // Llama al hook deleteUser con los parámetros definidos
  }

  // Mostrar mensaje API: DELETE (Muestra notificación de éxito o error)
  useEffect(() => {
    // Si la solicitud DELETE ha sido realizada
    if (deletedUser.isCalled) {
      // Función para mostrar un mensaje de éxito
      const successMessage = (message) =>
        toast.success(message, {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light'
        })

      // Función para mostrar un mensaje de error
      const errorMessage = (error) =>
        toast.error(error, {
          position: 'top-center',
          autoClose: 5000,
          closeOnClick: false,
          hideProgressBar: false,
          pauseOnHover: true,
          progress: undefined,
          theme: 'light'
        })

      // Si hay un mensaje de éxito en la respuesta de DELETE
      if (deletedUser.message) {
        successMessage(deletedUser.message)
        setLocation('/users/grid')
      }

      // Si hay un mensaje de error en la respuesta de DELETE
      if (deletedUser.error) {
        errorMessage(deletedUser.error)
      }
    }
  }, [deletedUser.loading]) // Este useEffect se ejecutará cada vez que 'deletedUser.loading' cambie

  // Si es un nuevo usuario o si el usuario existe
  return isNew || (!isNew && fields.id !== 0) ? (
    <>
      <div className="sm:flex sm:items-start sm:justify-between">
        <div className="flex items-start gap-x-3">
          <h2 className="text-lg font-medium text-gray-800 dark:text-white">
            Usuari {fields?.id !== 0 ? '#' + fields?.id : 'nou'}
          </h2>
        </div>

        <div className="flex items-center mt-4 sm:mt-0 gap-x-4">
          <button
            type="button"
            className="flex items-center justify-center w-1/2 px-5 py-2 text-sm text-white capitalize transition-colors duration-200 bg-[#006F53] border border-[#006F53] rounded-md sm:w-36 gap-x-2 enabled:hover:bg-[#e8f5f0] enabled:hover:text-[#006F53] dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-[#006F53] focus:border-[#006F53] dark:focus:border-[#006F53]"
            onClick={() => setLocation('/users/grid')}
          >
            <LuMoveLeft size={20} />
            <span>Tornar</span>
          </button>
        </div>
      </div>

      <UserFormDetail
        isNew={isNew}
        fields={fields}
        handleSubmit={handleSubmit}
        handleDelete={handleDelete}
      />
    </>
  ) : (
    <div className="flex items-center content-center justify-center w-full h-full">
      <p
        className="flex flex-col-reverse items-center justify-center h-full font-bold dark:text-[#F5F5F5] text-gray-700"
        title="Cargando..."
      >
        <ImSpinner9 size={70} className="animate-spin" />
      </p>
    </div>
  )
}

// Define los tipos de las props que recibe el componente actual
UserViewDetail.propTypes = {
  params: PropTypes.any
}
